import React, { useEffect, useState } from 'react';
import TaskCalendar from '../../components/calendar/TaskCalendar';
import { Tasks, User } from 'common/src/utils/types';
import { fetchTasks } from 'common/src/api/tasks';
import { fetchUsers } from 'common/src/api/users';
import { Select, SelectItem, Spinner } from '@nextui-org/react';
import useUserStore from '../../store/useUserStore';

const statusColors = {
  pending: '#FFA500',   // Orange
  accepted: '#4CAF50',  // Green
  assigned: '#2196F3',  // Blue
  progress: '#9C27B0',  // Purple
  complete: '#4CAF50',  // Green
  rejected: '#F44336',  // Red
};

const TaskCalendarPage: React.FC = () => {
  const [tasks, setTasks] = useState<Tasks[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { userProfile } = useUserStore();

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const [tasksResponse, usersResponse] = await Promise.all([fetchTasks(), fetchUsers()]);
        setTasks(tasksResponse.data || []);
        setUsers(usersResponse || []);
      } catch (error) {
        console.error('Error loading data:', error);
        setTasks([]);
        setUsers([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  const filteredTasks = tasks.filter(task => {
    if (selectedStatus && task.status !== selectedStatus) return false;
    if (selectedUser && task.assigned_user_id !== Number(selectedUser)) return false;
    return true;
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <Spinner size="lg" color="secondary" />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Select
              label="Filtrar por estado"
              placeholder="Seleccionar estado"
              selectedKeys={selectedStatus ? [selectedStatus] : []}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="w-full"
              items={[
                { key: 'all', value: '', label: 'Todos los estados' },
                ...Object.entries(statusColors).map(([status, color]) => ({
                  key: status,
                  value: status,
                  label: status.charAt(0).toUpperCase() + status.slice(1),
                  color: color
                }))
              ]}
            >
              {(item) => (
                <SelectItem key={item.key} value={item.value}>
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 rounded-full mr-2"
                      style={{ backgroundColor: 'color' in item ? item.color : '#757575' }}
                    />
                    {item.label}
                  </div>
                </SelectItem>
              )}
            </Select>
            
            {userProfile?.role.includes('admin') && (
              <Select
                label="Filtrar por usuario"
                placeholder="Seleccionar usuario"
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full"
                items={[
                  { label: 'Todos los usuarios', value: '' },
                  ...users.map((user) => ({
                    label: `${user.first_name} ${user.last_name}`,
                    value: user.id.toString(),
                  })),
                ]}
              >
                {(user) => (
                  <SelectItem key={user.value} value={user.value}>
                    {user.label}
                  </SelectItem>
                )}
              </Select>
            )}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <TaskCalendar tasks={filteredTasks} isLoading={isLoading} statusColors={statusColors} />
        </div>
      </div>
    </div>
  );
};

export default TaskCalendarPage;